import { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { calendarApi, settingsApi } from '../api';
import {
  Empty, ErrorNote, Modal, Spinner, dateTh, money, shopInstantIso, th, useLoader,
} from '../components/common';
import { BedPicker, TherapistPicker } from '../components/pickers';
import {
  IconSearch, IconCalendar, IconGuest, IconChevronDown, IconPin,
  IconMessage, IconTrash,
} from '../components/icons';
import { CATALOG_FALLBACK, SERVICE_IMAGES } from '../catalogImages';

const STEPS = [
  { key: 'service', label: 'บริการ' },
  { key: 'therapist', label: 'หมอนวด' },
  { key: 'bed', label: 'เตียง' },
];

const GENDERS = [
  { value: 'female', glyph: '♀', className: 'g-female' },
  { value: 'male', glyph: '♂', className: 'g-male' },
  { value: 'other', glyph: '⚧', className: 'g-other' },
];

const blankGuest = (seed = {}) => ({
  customerId: seed.customerId || null,
  name: seed.name || '',
  phone: seed.phone || '',
  gender: seed.gender || '',
  ageBandId: seed.ageBandId || '',
  nationalityCode: seed.nationalityCode || '',
  // one row per chosen service — therapist and bed are filled in later steps
  lines: [],
});

/** First unassigned line, or the last line when changing an existing choice. */
const targetLine = (guest, key) => {
  const empty = guest?.lines?.findIndex((line) => !line[key]) ?? -1;
  return empty === -1 ? (guest?.lines?.length || 0) - 1 : empty;
};

const agencyPrice = (variant, agencyId) => {
  if (!agencyId) return Number(variant.price || 0);
  const configured = variant.agencyPrices?.[agencyId]
    ?? variant.agencyPrices?.[String(agencyId)];
  return configured === '' || configured == null
    ? Number(variant.price || 0)
    : Number(configured);
};

/**
 * Step 2 of จองเลย — the booking builder.
 *
 * Left: the catalog (บริการ / สินค้า). Right: one card per guest, walked
 * through บริการ → หมอนวด → เตียง. Nothing is required beyond picking at least
 * one service, matching how the shop actually takes walk-ins.
 */
export default function BookingBuilder() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const info = state?.info;
  const [showMore, setShowMore] = useState(false);

  const menu = useLoader(() => calendarApi.menu(), []);
  const lookups = useLoader(() => calendarApi.lookups(), []);
  const allTherapists = useLoader(() => settingsApi.therapists(), []);

  const [tab, setTab] = useState('service');
  const [step, setStep] = useState('service');
  const [category, setCategory] = useState('');
  const [search, setSearch] = useState('');
  const [guests, setGuests] = useState(() => [blankGuest(info?.guest)]);
  const [active, setActive] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [genderFilter, setGenderFilter] = useState('');
  const [paymentChoiceOpen, setPaymentChoiceOpen] = useState(false);

  // Availability must be known before the admin clicks. Both endpoints receive
  // the exact booking window; without `at`, getBeds deliberately treats every
  // bed as free because the room-layout screen uses the same endpoint.
  const activeGuest = guests[active] || blankGuest();
  const therapistLineIndex = targetLine(activeGuest, 'therapistId');
  const bedLineIndex = targetLine(activeGuest, 'bedId');
  const therapistLine = activeGuest.lines[therapistLineIndex];
  const bedLine = activeGuest.lines[bedLineIndex];
  const bookingStart = info ? shopInstantIso(info.date, info.time) : null;
  const therapistMinutes = Number(therapistLine?.durationMinutes) || 60;
  const bedMinutes = Number(bedLine?.durationMinutes) || 60;
  const activeVariantId = bedLine?.serviceVariantId || null;

  const therapistAvailability = useLoader(
    () => bookingStart
      ? calendarApi.therapists(bookingStart, therapistMinutes)
      : Promise.resolve({ therapists: [] }),
    [bookingStart, therapistMinutes, therapistLine?.serviceVariantId]
  );
  const beds = useLoader(
    () => calendarApi.beds(activeVariantId, {
      at: bookingStart,
      minutes: bedMinutes,
    }),
    [activeVariantId, bookingStart, bedMinutes]
  );

  const categories = useMemo(() => {
    const set = new Map();
    (menu.data?.menu || []).forEach((s) => {
      if (s.category_name_th) set.set(s.category_name_th, true);
    });
    return [...set.keys()];
  }, [menu.data]);

  // Deep-linked here without going through ข้อมูลการจอง. Checked after the
  // hooks above so their call order is identical on every render.
  if (!info) {
    return (
      <div className="card">
        <Empty icon="appointment" message="เริ่มการจองจากหน้าหลัก" />
        <div style={{ textAlign: 'center', paddingBottom: 24 }}>
          <button className="btn-solid" onClick={() => navigate('/')}>กลับหน้าหลัก</button>
        </div>
      </div>
    );
  }

  const services = (menu.data?.menu || []).filter((s) => {
    if (tab === 'product') return false;
    if (category && s.category_name_th !== category) return false;
    const q = search.trim().toLowerCase();
    return !q || `${s.name_en} ${s.name_th || ''}`.toLowerCase().includes(q);
  });

  const setGuest = (i, patch) =>
    setGuests((g) => g.map((x, n) => (n === i ? { ...x, ...patch } : x)));

  const addLine = (service, variant) => {
    setGuests((g) => g.map((x, n) => (n === active
      ? {
        ...x,
        lines: [...x.lines, {
          serviceVariantId: variant.id,
          name: `${service.name_en}${service.name_th ? ` ${service.name_th}` : ''}`,
          durationMinutes: variant.durationMinutes,
          price: agencyPrice(variant, info.agencyId),
          imageUrl: service.image_url || SERVICE_IMAGES[service.code] || CATALOG_FALLBACK,
          // every duration of this service, so the chips stay switchable
          options: service.variants,
          therapistId: '',
          bedId: '',
        }],
      }
      : x)));
  };

  const usedOutsideTarget = (key, lineIndex) => guests.flatMap((guest, guestIndex) =>
    guest.lines
      .filter((_, index) => !(guestIndex === active && index === lineIndex))
      .map((line) => line[key])
      .filter(Boolean)
  );
  const blockedTherapistIds = usedOutsideTarget('therapistId', therapistLineIndex);
  const blockedBedIds = usedOutsideTarget('bedId', bedLineIndex);
  const blockedTherapistSet = new Set(blockedTherapistIds.map(String));
  const blockedBedSet = new Set(blockedBedIds.map(String));
  const availableTherapists = therapistAvailability.data?.therapists || [];
  const therapistById = new Map(availableTherapists.map((t) => [String(t.id), t]));

  const pickTherapist = (t) => {
    const id = t.id ?? t.therapistId;
    const availability = therapistById.get(String(id));
    if (!availability?.isFree || !availability.onDuty || blockedTherapistSet.has(String(id))) return;
    const g = guests[active];
    const li = targetLine(g, 'therapistId');
    if (li < 0) return;
    setLine(active, li, { therapistId: id });
  };

  const pickBed = (b) => {
    if (!b.supportsService || !b.isFree || blockedBedSet.has(String(b.id))) return;
    const g = guests[active];
    const li = targetLine(g, 'bedId');
    if (li < 0) return;
    setLine(active, li, { bedId: b.id });
  };

  const setLine = (gi, li, patch) =>
    setGuests((g) => g.map((x, n) => (n === gi
      ? { ...x, lines: x.lines.map((l, m) => (m === li ? { ...l, ...patch } : l)) }
      : x)));

  const lanes = availableTherapists.filter((t) => t.onDuty);

  const total = guests.reduce(
    (s, g) => s + g.lines.reduce((n, l) => n + Number(l.price || 0), 0), 0,
  );
  const lineCount = guests.reduce((s, g) => s + g.lines.length, 0);

  // The header shows when the party is expected to finish.
  const longest = Math.max(0, ...guests.map(
    (g) => g.lines.reduce((n, l) => n + Number(l.durationMinutes || 0), 0),
  ));
  const endTime = (() => {
    const [h, m] = (info.time || '00:00').split(':').map(Number);
    const t = h * 60 + m + longest;
    return `${String(Math.floor(t / 60) % 24).padStart(2, '0')}:${String(t % 60).padStart(2, '0')}`;
  })();

  const save = async ({ pay } = {}) => {
    if (!lineCount) return setError('กรุณาเลือกบริการอย่างน้อย 1 รายการ');

    setBusy(true);
    setError(null);
    try {
      const res = await calendarApi.createBooking({
        channel: info.channel,
        agencyId: info.agencyId || undefined,
        agencyRef: info.agencyRef || undefined,
        scheduledAt: shopInstantIso(info.date, info.time),
        note: info.note || undefined,
        booker: {
          customerId: info.guest?.customerId || undefined,
          name: info.guest?.name || undefined,
          phone: info.guest?.phone || undefined,
          isGuest1: info.bookerIsGuest1 !== false,
        },
        guests: guests.map((g) => ({
          customerId: g.customerId || undefined,
          displayName: g.name || undefined,
          phone: g.phone || undefined,
          gender: g.gender || 'unspecified',
          ageBandId: g.ageBandId || undefined,
          nationalityCode: g.nationalityCode || undefined,
          services: g.lines.map((l) => ({
            serviceVariantId: Number(l.serviceVariantId),
            bedId: l.bedId ? Number(l.bedId) : undefined,
            therapistIds: l.therapistId ? [Number(l.therapistId)] : [],
          })),
        })),
      });

      // Pay-now reuses the schedule drawer's complete payment flow (cash,
      // PromptPay/Beam and card). Pay-later leaves the booking unpaid.
      if (pay && res.appointmentIds?.length) {
        navigate('/', {
          state: {
            openAppointmentId: res.appointmentIds[0],
            autoPay: true,
          },
        });
      } else {
        navigate('/');
      }
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  if (menu.loading || beds.loading || therapistAvailability.loading) return <Spinner />;

  return (
    <div className="builder">
      {/* ── left: catalog ─────────────────────────────────────────── */}
      <aside className="builder-menu">
        <div className="tab-row" style={{ marginBottom: 12 }}>
          <button className={`tab${tab === 'service' ? ' active' : ''}`} onClick={() => setTab('service')}>
            บริการ {lineCount > 0 && <span className="tab-count">{lineCount}</span>}
          </button>
          <button className={`tab${tab === 'product' ? ' active' : ''}`} onClick={() => setTab('product')}>
            สินค้า{lineCount > 0 && tab === 'service' ? '' : ''}
          </button>
        </div>

        <div className="menu-filters">
          <label className="search-field">
            <IconSearch size={17} />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="ค้นหา" />
          </label>
          <select value={category} onChange={(e) => setCategory(e.target.value)}>
            <option value="">ทั้งหมด</option>
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        <div className="pick-banner">เลือกสำหรับลูกค้าคนที่ {active + 1}</div>

        {step === 'therapist' ? (
          /* เข้างาน first, then everyone else — the real panel splits on
             whether the therapist is on today's roster. */
          <TherapistPicker
            selectedIds={(guests[active]?.lines || []).map((l) => l.therapistId).filter(Boolean)}
            lanes={lanes}
            all={allTherapists.data?.therapists || []}
            availability={availableTherapists}
            blockedIds={blockedTherapistIds}
            requireOnDuty
            gender={genderFilter}
            onGender={setGenderFilter}
            onPick={pickTherapist}
          />
        ) : step === 'bed' ? (
          <BedPicker
            beds={beds.data?.beds || []}
            onPick={(b) => pickBed(b)}
            selectedIds={(guests[active]?.lines || []).map((l) => l.bedId).filter(Boolean)}
            blockedIds={blockedBedIds}
          />
        ) : tab === 'product' ? (
          <Empty icon="product" message="ขายสินค้าใช้ปุ่ม ซื้อสินค้า บนหน้าหลัก" />
        ) : services.length === 0 ? (
          <Empty icon="treatment" message="ไม่พบบริการ" />
        ) : (
          Object.entries(services.reduce((acc, x) => {
            const k = x.category_name_th || 'อื่น ๆ';
            (acc[k] ||= []).push(x);
            return acc;
          }, {})).map(([group, items]) => (
            <div key={group}>
              <div className="menu-group">{group}</div>
              {items.map((x) => (
                <div className="menu-card" key={x.service_id}>
                  <img
                    className="menu-thumb"
                    src={x.image_url || SERVICE_IMAGES[x.code] || CATALOG_FALLBACK}
                    alt={x.name_th || x.name_en}
                    onError={(event) => {
                      event.currentTarget.onerror = null;
                      event.currentTarget.src = CATALOG_FALLBACK;
                    }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="menu-name">{x.name_en} {x.name_th}</div>
                    <div className="duration-row">
                      {x.variants.map((v) => {
                        const used = guests[active]?.lines
                          .filter((l) => l.serviceVariantId === v.id).length;
                        return (
                          <button
                            key={v.id}
                            className="duration-chip"
                            onClick={() => addLine(x, v)}
                            title={`${money(agencyPrice(v, info.agencyId))} บาท`}
                          >
                            {v.durationMinutes}
                            {used > 0 && <span className="chip-count">{used}</span>}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ))
        )}

        {/* Drives the stepper — the catalog itself never changes. */}
        <button
          className="btn-solid next-step"
          disabled={!lineCount || step === 'bed'}
          onClick={() => setStep(step === 'service' ? 'therapist' : 'bed')}
        >
          ถัดไป
        </button>
      </aside>

      {/* ── right: the booking being built ────────────────────────── */}
      <section className="builder-main">
        <div className="builder-head">
          <strong>{th(info.channel)}</strong>
          <button
            className="btn-ghost btn-sm"
            style={{ marginLeft: 'auto' }}
            onClick={() => setShowMore((v) => !v)}
          >
            <IconChevronDown size={15} className={showMore ? 'flip' : ''} />
            {showMore ? 'ย่อ' : 'ดูเพิ่มเติม'}
          </button>
        </div>

        {showMore && (
          <div className="builder-more">
            <div><span>ช่องทางการจอง</span><b>{th(info.channel)}</b></div>
            {info.agencyName && <div><span>เอเจนซี่</span><b>{info.agencyName}</b></div>}
            {info.agencyRef && <div><span>รหัสเอเจนซี่</span><b>{info.agencyRef}</b></div>}
            <div><span>วันที่จอง</span><b>{dateTh(info.date)}</b></div>
            <div><span>เวลา</span><b>{info.time} - {endTime} น.</b></div>
            <div><span>ผู้จอง</span><b>{info.guest?.name || 'ไม่ระบุชื่อ'}</b></div>
            <div><span>เบอร์โทรศัพท์</span><b>{info.guest?.phone || '-'}</b></div>
            <div><span>จำนวนลูกค้า</span><b>{guests.length} คน</b></div>
            <div><span>หมายเหตุ</span><b>{info.note || '-'}</b></div>
          </div>
        )}
        <div className="builder-when">
          <IconCalendar size={16} />
          <span>{dateTh(info.date)} ({info.time} - {endTime})</span>
        </div>
        {info.note && (
          <div className="builder-note">
            <IconMessage size={15} />
            <span>{info.note}</span>
          </div>
        )}

        <div className="stepper">
          {STEPS.map((st, i) => {
            const current = STEPS.findIndex((x) => x.key === step);
            const done = i < current;
            return (
              <button
                key={st.key}
                className={`step${step === st.key ? ' on' : ''}${done ? ' done' : ''}`}
                /* Only backwards: the wizard advances itself as each step is
                   satisfied, so a forward jump would skip a required choice. */
                disabled={i > current}
                onClick={() => done && setStep(st.key)}
              >
                <span className="step-no">{done ? '✓' : i + 1}</span>
                {st.label}
              </button>
            );
          })}
        </div>

        {error && <ErrorNote error={{ message: error }} />}

        {guests.map((g, gi) => (
          <div
            className={`guest-card${active === gi ? ' active' : ''}`}
            key={gi}
            onClick={() => setActive(gi)}
          >
            <div className="guest-no">{gi + 1}</div>

            <div className="guest-body">
              <div className="ident-row">
                <input
                  value={g.phone}
                  onChange={(e) => setGuest(gi, { phone: e.target.value })}
                  placeholder="เบอร์โทรศัพท์"
                />
                <input
                  value={g.name}
                  onChange={(e) => setGuest(gi, { name: e.target.value })}
                  placeholder="ลูกค้า"
                />
                {GENDERS.map((x) => (
                  <button
                    key={x.value}
                    className={`gender-btn ${x.className}${g.gender === x.value ? ' on' : ''}`}
                    onClick={() => setGuest(gi, { gender: g.gender === x.value ? '' : x.value })}
                  >
                    {x.glyph}
                  </button>
                ))}
                <select value={g.ageBandId} onChange={(e) => setGuest(gi, { ageBandId: e.target.value })}>
                  <option value="">อายุ</option>
                  {(lookups.data?.ageBands || []).map((b) => (
                    <option key={b.id} value={b.id}>{b.label_th || b.label_en}</option>
                  ))}
                </select>
                <select
                  value={g.nationalityCode}
                  onChange={(e) => setGuest(gi, { nationalityCode: e.target.value })}
                >
                  <option value="">สัญชาติ</option>
                  {(lookups.data?.nationalities || []).map((n) => (
                    <option key={n.code} value={n.code}>{n.name_th || n.name_en}</option>
                  ))}
                </select>
                {guests.length > 1 && (
                  <button
                    className="btn-ghost btn-sm"
                    onClick={() => {
                      setGuests((x) => x.filter((_, n) => n !== gi));
                      setActive(0);
                    }}
                  >
                    ✕
                  </button>
                )}
              </div>

              {/* บริการ / หมอนวด / เตียง sit side by side; the service box
                  fills first and the other two stay greyed until their step. */}
              <div className="slot-row">
                <div
                  className={`slot service${step === 'service' ? ' on' : ''}`}
                  onClick={() => setStep('service')}
                >
                  {g.lines.length === 0 ? (
                    <div className="slot-empty">
                      <strong>บริการ</strong>
                      <span>กรุณาเลือกบริการด้านซ้าย</span>
                    </div>
                  ) : (
                    <>
                      {g.lines.map((l, li) => (
                        <div className="svc-card" key={li}>
                          <img
                            className="svc-thumb"
                            src={l.imageUrl || CATALOG_FALLBACK}
                            alt={l.name}
                            onError={(event) => {
                              event.currentTarget.onerror = null;
                              event.currentTarget.src = CATALOG_FALLBACK;
                            }}
                          />
                          <div className="svc-body">
                            <div className="svc-head">
                              <strong>{l.name}</strong>
                              <button
                                className="svc-del"
                                title="ลบ"
                                onClick={() => setGuest(gi, {
                                  lines: g.lines.filter((_, m) => m !== li),
                                })}
                              >
                                <IconTrash size={16} />
                              </button>
                            </div>
                            <div className="duration-row">
                              {(l.options || [{ id: l.serviceVariantId, durationMinutes: l.durationMinutes, price: l.price }])
                                .map((o) => (
                                  <button
                                    key={o.id}
                                    className={`duration-chip${o.id === l.serviceVariantId ? ' on' : ''}`}
                                    onClick={() => setLine(gi, li, {
                                      serviceVariantId: o.id,
                                      durationMinutes: o.durationMinutes,
                                      price: agencyPrice(o, info.agencyId),
                                      // A different duration means a different
                                      // availability window. Force both choices
                                      // through the freshly disabled pickers.
                                      therapistId: '',
                                      bedId: '',
                                    })}
                                  >
                                    {o.durationMinutes}
                                  </button>
                                ))}
                            </div>
                          </div>
                        </div>
                      ))}
                      <button className="add-service" onClick={() => setActive(gi)}>
                        ⊕ เพิ่มบริการ
                      </button>
                    </>
                  )}
                </div>

                <div
                  className={`slot pickable${step === 'therapist' ? ' on' : ''}`}
                  onClick={() => g.lines.length && setStep('therapist')}
                >
                  {g.lines.length === 0 ? (
                    <div className="slot-ghost"><IconGuest size={20} /><span>หมอนวด</span></div>
                  ) : g.lines.map((l, li) => {
                    const picked = lanes.find((t) => String(t.therapistId) === String(l.therapistId))
                      || (allTherapists.data?.therapists || []).find((t) => String(t.id) === String(l.therapistId));
                    if (l.therapistId) {
                      return (
                        <div className="slot-filled" key={li}>
                          <strong>{picked?.nicknameEn || picked?.nickname_en || '—'}</strong>
                        </div>
                      );
                    }
                    if (step !== 'therapist') {
                      return <div className="slot-ghost" key={li}><IconGuest size={20} /><span>หมอนวด</span></div>;
                    }
                    return (
                      <div className="slot-empty slot-empty-prompt" key={li}>
                        <IconGuest size={20} />
                        <strong>เลือกหมอนวดด้านซ้าย</strong>
                      </div>
                    );
                  })}
                </div>

                <div
                  className={`slot pickable${step === 'bed' ? ' on' : ''}`}
                  onClick={() => g.lines.length && setStep('bed')}
                >
                  {g.lines.length === 0 ? (
                    <div className="slot-ghost"><IconPin size={20} /><span>เตียง</span></div>
                  ) : g.lines.map((l, li) => {
                    const bed = (beds.data?.beds || []).find((b) => String(b.id) === String(l.bedId));
                    if (l.bedId) {
                      return <div className="slot-filled" key={li}><strong>{bed?.code || '—'}</strong></div>;
                    }
                    if (step !== 'bed') {
                      return <div className="slot-ghost" key={li}><IconPin size={20} /><span>เตียง</span></div>;
                    }
                    return (
                      <div className="slot-empty slot-empty-prompt" key={li}>
                        <IconPin size={20} />
                        <strong>เลือกเตียงด้านซ้าย</strong>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        ))}

        <button
          className="btn-outline add-guest-btn"
          onClick={() => { setGuests((g) => [...g, blankGuest()]); setActive(guests.length); }}
        >
          <IconGuest size={18} />
          <span>เพิ่มลูกค้าอีกคน</span>
        </button>

        <div className="builder-foot">
          <span className="foot-total">ราคารวม <strong>{money(total)}</strong></span>
          <button className="btn-outline" disabled={busy} onClick={() => save()}>
            {busy ? 'กำลังบันทึก…' : 'บันทึก'}
          </button>
          <button
            className="btn-solid"
            disabled={busy}
            onClick={() => {
              if (!lineCount) {
                setError('กรุณาเลือกบริการอย่างน้อย 1 รายการ');
                return;
              }
              setPaymentChoiceOpen(true);
            }}
          >
            ชำระเงิน
          </button>
        </div>
      </section>

      {paymentChoiceOpen && (
        <Modal
          className="payment-choice-modal"
          title="เลือกการชำระเงิน"
          onClose={() => !busy && setPaymentChoiceOpen(false)}
          footer={(
            <>
              <button
                className="btn-outline"
                disabled={busy}
                onClick={() => {
                  setPaymentChoiceOpen(false);
                  save();
                }}
              >
                ข้ามและชำระภายหลัง
              </button>
              <button
                className="btn-solid"
                disabled={busy}
                onClick={() => {
                  setPaymentChoiceOpen(false);
                  save({ pay: true });
                }}
              >
                ชำระตอนนี้
              </button>
            </>
          )}
        >
          <div className="payment-choice-copy">
            <strong>ต้องการรับชำระเงินตอนนี้หรือไม่?</strong>
            <p>
              เลือกชำระภายหลังเพื่อบันทึกการจองเป็น “ยังไม่ชำระ”
              และกลับมารับเงินจากรายการบนตารางเวลาได้ภายหลัง
            </p>
          </div>
        </Modal>
      )}
    </div>
  );
}
